import { memo, useCallback, useState } from 'react';

import {
  CopyIcon,
  RefreshCcwIcon,
  Trash2Icon,
  EllipsisVerticalIcon,
  CloudUploadIcon,
  DownloadIcon,
  SquarePenIcon,
  ArrowRightLeftIcon,
  TextSearchIcon,
  EyeIcon,
  PencilLineIcon,
  MessagesSquareIcon,
  FileIcon,
  FolderInputIcon,
  FilePenLineIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { MODAL_DOCUMENT_PREVIEW } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SpinnerIcon } from '@/components/ui/spinner';
import useDatasources from '@/hooks/useDatasources';
import { useAPIDraftDocuments } from '@/modules/collections/hooks/useAPIDraftDocuments';
import { useUpdateDatasourceDraft } from '@/modules/collections/hooks/useUpdateDatasourceDraft';
import { openDeleteDatasourceModal, openDeleteDocumentModal } from '@/redux/collections/actions';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { trackEvent } from '@/segment/segment';
import { Datasource, DatasourceType } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

import {
  DOCUMENT_TYPES_WITH_PREVIEW,
  ROOT_DATASOURCE_NAMES,
  ROOT_DATASOURCE_TYPES,
} from '../../../constants';
import { appendEllipsis, downloadDocument, isDocumentRow } from '../../../helper';
import { useAPIDocuments } from '../../../hooks/useAPIDocuments';
import { useTabCategory } from '../../../hooks/useCategory';
import { DatasourceDocument } from '../../../types';
import { MoveToFolder } from '../MoveToFolder';

interface ActionsCellProps {
  item: DatasourceDocument;
}

const ActionsCellComponent = ({ item }: ActionsCellProps) => {
  const { collectionId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { publishDocuments, draftDocuments } = useAPIDraftDocuments({
    collection_id: collectionId,
    enabled: false,
  });
  const { publishAllDocuments, draftAllDocuments } = useUpdateDatasourceDraft({
    collection_id: collectionId,
  });
  const isFolder = !isDocumentRow(item);
  const { isFAQTab, isDocumentsTab, isWebsiteTab, isExternalTab } = useTabCategory();
  const showEditOption =
    ((isFAQTab || isDocumentsTab) && isDocumentRow(item)) ||
    (!isDocumentRow(item) && (isExternalTab || isWebsiteTab));
  const showDownloadOption = (isFAQTab || isDocumentsTab) && isDocumentRow(item);
  const showReplaceOption = (isFAQTab || isDocumentsTab) && isDocumentRow(item);
  const showSyncOption = (isWebsiteTab || isExternalTab) && !isDocumentRow(item);
  const showViewReportOption = (isWebsiteTab || isExternalTab) && !isDocumentRow(item);
  const showPreviewOption =
    isDocumentsTab && DOCUMENT_TYPES_WITH_PREVIEW.includes(item.type) && isDocumentRow(item);
  const showRenameFolderOption = !isDocumentRow(item);
  const showConvertToFAQOption = isDocumentsTab && isDocumentRow(item) && item.type === 'txt';
  const showMoveToFolderOption = (isDocumentsTab || isFAQTab) && isDocumentRow(item);

  const {
    datasources: allDatasources,
    syncDatasource,
    createDatasourceAsync,
  } = useDatasources(collectionId);
  const { moveDocument } = useAPIDocuments({ collection_id: collectionId });

  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.DELETE)
  );
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.READ)
  );
  const slug = useSelector(selectAccountSlug);

  // Move to folder functionality
  const targetDatasources = allDatasources?.filter(
    (ds) =>
      ((isFAQTab && ds.type === DatasourceType.FAQ && ds.name !== ROOT_DATASOURCE_NAMES.faq) ||
        (isDocumentsTab &&
          ds.type === DatasourceType.FILES &&
          ds.name !== ROOT_DATASOURCE_NAMES.documents)) &&
      ds.datasource_id !== item.datasource_id
  );
  const [isSelectingFolder, setIsSelectingFolder] = useState(false);
  const [isPublishAllLoading, setIsPublishAllLoading] = useState(false);
  const [isDraftAllLoading, setIsDraftAllLoading] = useState(false);

  // Conditional render options
  const showPublishOption = item.is_draft && !isFolder;
  const showDraftOption = !item.is_draft && !isFolder;
  const showBulkActions = isFolder;

  const handleEdit = useCallback(() => {
    if (isFAQTab) {
      dispatch(setSidebarContent('add_faq'));
    } else if (isDocumentsTab) {
      if (item.type === 'txt') {
        dispatch(setSidebarContent('add_txt_document'));
      } else {
        dispatch(setSidebarContent('edit_document'));
      }
    } else if (isExternalTab) {
      dispatch(setSidebarContent('add_external_sources'));
    } else if (isWebsiteTab) {
      dispatch(setSidebarContent('add_webpages'));
    }
    dispatch(setDatasourceId(item.datasource_id));
    dispatch(setDocumentId(item.document_id));
  }, [
    dispatch,
    item.datasource_id,
    item.document_id,
    isFAQTab,
    isDocumentsTab,
    isExternalTab,
    isWebsiteTab,
    item.type,
  ]);

  const handleCopyId = useCallback(async () => {
    let id: string;
    if (isDocumentRow(item)) {
      trackEvent(EventName.ClickCopyDocumentId, { document_id: item.document_id });
      id = item.document_id;
    } else {
      trackEvent(EventName.ClickCopyDatasourceId, { datasource_id: item.datasource_id });
      id = item.datasource_id;
    }

    try {
      await navigator.clipboard.writeText(id);
      toast.success(t('common.copied'), { duration: 1000 });
    } catch {
      toast.error(t('collections.error_copying_id'), { duration: 4000 });
    }
  }, [item, t]);

  const handlePublish = useCallback(async () => {
    await publishDocuments({ document_ids: [item.document_id], datasource_id: item.datasource_id });
  }, [item.document_id, item.datasource_id, publishDocuments]);

  const handleDraft = useCallback(async () => {
    await draftDocuments({ document_ids: [item.document_id], datasource_id: item.datasource_id });
  }, [item.document_id, item.datasource_id, draftDocuments]);

  const handlePublishAll = useCallback(async () => {
    if (isPublishAllLoading) {
      return;
    }

    setIsPublishAllLoading(true);
    const datasource = allDatasources?.find((ds) => ds.datasource_id === item.datasource_id);

    try {
      if (datasource) {
        await publishAllDocuments({
          datasource_id: item.datasource_id,
          type: datasource.type,
          config: datasource.config,
        });
      }
    } finally {
      setIsPublishAllLoading(false);
    }
  }, [item.datasource_id, publishAllDocuments, allDatasources, isPublishAllLoading]);

  const handleDraftAll = useCallback(async () => {
    if (isDraftAllLoading) {
      return;
    }

    setIsDraftAllLoading(true);
    const datasource = allDatasources?.find((ds) => ds.datasource_id === item.datasource_id);

    try {
      if (datasource) {
        await draftAllDocuments({
          datasource_id: item.datasource_id,
          type: datasource.type,
          config: datasource.config,
        });
      }
    } finally {
      setIsDraftAllLoading(false);
    }
  }, [item.datasource_id, draftAllDocuments, allDatasources, isDraftAllLoading]);

  const handleDownload = useCallback(() => {
    downloadDocument(item, collectionId, slug, isFAQTab ? 'csv' : undefined);
  }, [collectionId, item, slug, isFAQTab]);

  const handleReplace = useCallback(() => {
    const { document_id, datasource_id } = item;

    dispatch(setDocumentId(document_id));
    dispatch(setDatasourceId(datasource_id));

    if (isFAQTab) {
      dispatch(setSidebarContent('upload_faq'));
    } else if (isDocumentsTab) {
      dispatch(setSidebarContent('upload_documents'));
    }
  }, [item, isFAQTab, isDocumentsTab, dispatch]);

  const handlePreview = useCallback(() => {
    const { document_id, datasource_id } = item;

    dispatch(
      pushModal(MODAL_DOCUMENT_PREVIEW, { document_id, datasource_id, collection_id: collectionId })
    );
  }, [collectionId, dispatch, item]);

  const handleDelete = useCallback(() => {
    if (isDocumentRow(item)) {
      dispatch(
        openDeleteDocumentModal({
          datasource_id: item.datasource_id,
          document_id: item.document_id,
        })
      );
    } else {
      dispatch(openDeleteDatasourceModal(item.datasource_id));
    }
  }, [item, dispatch]);

  const handleSync = useCallback(() => {
    syncDatasource(item as unknown as Datasource);
  }, [item, syncDatasource]);

  const handleViewReport = useCallback(() => {
    dispatch(setDatasourceId(item.datasource_id));
    dispatch(setSidebarContent('ingestion_report'));
  }, [dispatch, item.datasource_id]);

  const handleRenameFolder = useCallback(() => {
    dispatch(setDatasourceId(item.datasource_id));
    dispatch(setSidebarContent('rename_folder'));
  }, [dispatch, item.datasource_id]);

  const handleConvertToFAQ = useCallback(async () => {
    const rootFAQDatasourceName = ROOT_DATASOURCE_TYPES[DatasourceType.FAQ].name;

    let rootFAQDatasourceId = allDatasources?.find(
      (ds) => ds.name === rootFAQDatasourceName
    )?.datasource_id;

    if (!rootFAQDatasourceId) {
      try {
        const rootFAQDatasource = await createDatasourceAsync(
          ROOT_DATASOURCE_TYPES[DatasourceType.FAQ]
        );
        rootFAQDatasourceId = rootFAQDatasource.datasource_id;
      } catch (_) {
        toast.error(t('collections.error_creating_faq_datasource'));
        return;
      }
    }

    moveDocument({
      document_id: item.document_id,
      new_datasource_id: rootFAQDatasourceId,
      datasource_id: item.datasource_id,
      name: item.name,
    });

    toast.success(t('collections.document_converted_to_faq'), {
      description: t('collections.document_converted_to_faq_description'),
    });
  }, [
    allDatasources,
    createDatasourceAsync,
    item.datasource_id,
    item.document_id,
    item.name,
    moveDocument,
    t,
  ]);

  const handleMoveToFolder = (e: Event) => {
    // Prevent menu from closing when selecting folder
    e.preventDefault();
    setIsSelectingFolder(true);
  };

  const handleMoveToFolderSubmit = async (
    data: { datasource_id: string },
    event?: React.BaseSyntheticEvent
  ) => {
    if (event) {
      event.preventDefault();
    }
    moveDocument({
      document_id: item.document_id,
      new_datasource_id: data.datasource_id,
      datasource_id: item.datasource_id,
      name: item.name,
    });
    setIsSelectingFolder(false);
  };

  return (
    <DropdownMenu
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          setIsSelectingFolder(false);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          onClick={(e) => {
            // Prevent from navigating to the collection page
            e.preventDefault();
            e.stopPropagation();
          }}
          variant="ghost-neutral"
          size="icon-sm"
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => {
          // Prevent handleRowClick from being executed
          e.stopPropagation();
        }}
      >
        {isSelectingFolder ? (
          <MoveToFolder datasources={targetDatasources} onSubmit={handleMoveToFolderSubmit} />
        ) : (
          <>
            {/* Conditional options */}
            {showEditOption && (
              <DropdownMenuItem
                onSelect={handleEdit}
                disabled={!canRead}
                className="flex items-center gap-2"
              >
                <SquarePenIcon size={16} className="text-muted-foreground" />
                {appendEllipsis(t('common.edit'))}
              </DropdownMenuItem>
            )}

            {showSyncOption && (
              <DropdownMenuItem
                onSelect={handleSync}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <RefreshCcwIcon size={16} className="text-muted-foreground" />
                {t('collections.sync')}
              </DropdownMenuItem>
            )}

            {showRenameFolderOption && (
              <DropdownMenuItem
                disabled={!canWrite}
                onSelect={handleRenameFolder}
                className="flex items-center gap-2"
              >
                <PencilLineIcon size={16} className="text-muted-foreground" />
                {appendEllipsis(t('common.rename'))}
              </DropdownMenuItem>
            )}

            {showDownloadOption && (
              <DropdownMenuItem
                onSelect={handleDownload}
                disabled={!canRead}
                className="flex items-center gap-2"
              >
                <DownloadIcon size={16} className="text-muted-foreground" />
                {t('collections.download')}
              </DropdownMenuItem>
            )}

            {showReplaceOption && (
              <DropdownMenuItem
                onSelect={handleReplace}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <ArrowRightLeftIcon size={16} className="text-muted-foreground" />
                {appendEllipsis(t('collections.replace'))}
              </DropdownMenuItem>
            )}

            {showViewReportOption && (
              <DropdownMenuItem
                onSelect={handleViewReport}
                disabled={!canRead}
                className="flex items-center gap-2"
              >
                <TextSearchIcon size={16} className="text-muted-foreground" />
                {t('collections.view_report')}
              </DropdownMenuItem>
            )}

            {showPreviewOption && (
              <DropdownMenuItem
                onSelect={handlePreview}
                disabled={!canRead}
                className="flex items-center gap-2"
              >
                <EyeIcon size={16} className="text-muted-foreground" />
                {t('common.preview')}
              </DropdownMenuItem>
            )}

            {showConvertToFAQOption && (
              <DropdownMenuItem
                onSelect={handleConvertToFAQ}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <MessagesSquareIcon size={16} className="text-muted-foreground" />
                {t('collections.convert_to_faq')}
              </DropdownMenuItem>
            )}

            {showMoveToFolderOption && targetDatasources?.length > 0 && (
              <DropdownMenuItem
                onSelect={handleMoveToFolder}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <FolderInputIcon size={16} className="text-muted-foreground" />
                {appendEllipsis(t('collections.move_to_folder'))}
              </DropdownMenuItem>
            )}

            {/* Common options */}
            {/* Publish or return to draft for single document */}
            {showPublishOption && (
              <DropdownMenuItem
                onSelect={handlePublish}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <CloudUploadIcon size={16} className="text-muted-foreground" />
                {t('collections.publish')}
              </DropdownMenuItem>
            )}

            {showDraftOption && (
              <DropdownMenuItem
                onSelect={handleDraft}
                disabled={!canWrite}
                className="flex items-center gap-2"
              >
                <FilePenLineIcon size={16} className="text-muted-foreground" />
                {t('collections.mark_as_draft')}
              </DropdownMenuItem>
            )}

            {/* Publish or return to draft for folder */}
            {showBulkActions && (
              <>
                <DropdownMenuItem
                  onSelect={handlePublishAll}
                  disabled={!canWrite || isPublishAllLoading}
                  className="flex items-center gap-2"
                >
                  {isPublishAllLoading ? (
                    <SpinnerIcon className="size-4 text-foreground" />
                  ) : (
                    <CloudUploadIcon size={16} className="text-muted-foreground" />
                  )}
                  {t('collections.publish_all')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleDraftAll}
                  disabled={!canWrite || isDraftAllLoading}
                  className="flex items-center gap-2"
                >
                  {isDraftAllLoading ? (
                    <SpinnerIcon className="size-4 text-foreground" />
                  ) : (
                    <FileIcon size={16} className="text-muted-foreground" />
                  )}
                  {t('collections.mark_all_as_draft')}
                </DropdownMenuItem>
              </>
            )}

            <DropdownMenuItem onSelect={handleCopyId} className="flex items-center gap-2">
              <CopyIcon size={16} className="text-muted-foreground" />
              {t('collections.datasources.copy_id')}
            </DropdownMenuItem>

            <DropdownMenuItem
              onSelect={handleDelete}
              disabled={!canDelete}
              variant="destructive"
              className="flex items-center gap-2"
            >
              <Trash2Icon size={16} />
              {appendEllipsis(t('common.delete'))}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ActionsCell = memo(ActionsCellComponent);

export default ActionsCell;
